import {
  EmailTemplateDto,
  EmailTemplateContentDto,
  EmailTemplateBlockDto,
} from "@babackapp/private-api-client/generated/schema";
import {
  EmailCardContainer,
  EmailMainContainer,
} from "@babackapp/blocks-editor/components/layout";
import defaultEmailsTheme from "@babackapp/blocks-editor/constants/default-emails-theme";
import type { BlockType } from "@babackapp/blocks-editor/types";
import { getEditorBlockConfig } from "@babackapp/editor-blocks-library/blocks";
import { BlockThemeProvider } from "@babackapp/editor-blocks-library/context";
import {
  createFormatter,
  Formatter,
  TEMPLATE_VARIABLES,
  VariablesDictionaryProvider,
  type VariableDefinition,
} from "@babackapp/variables-registry";
import { cn } from "@babackapp/ui/lib/utils";
import useCurrentShopSuspense from "../hooks/use-current-shop-suspense";
import { ImageIcon } from "@radix-ui/react-icons";

type NotificationTemplatePreviewProps = {
  template: EmailTemplateDto | null;
  viewport?: "desktop" | "mobile";
  className?: string;
  /**
   * Variable set used for preview example values + dictionary. Defaults to the
   * tracking variables; the return preview passes the return variable set.
   */
  variables?: VariableDefinition[];
};

/**
 * A read-only preview of a notification template.
 * Uses the block renderers directly without editor context dependencies.
 */
export function NotificationTemplatePreview({
  template,
  viewport = "desktop",
  className,
  variables = TEMPLATE_VARIABLES,
}: NotificationTemplatePreviewProps) {
  const { data: shop } = useCurrentShopSuspense();

  if (!template) {
    return (
      <div
        className={cn(
          "flex items-center justify-center h-full text-muted-foreground",
          className,
        )}
      >
        <ImageIcon className="size-8 opacity-50" />
      </div>
    );
  }

  const data = template.content as EmailTemplateContentDto;
  const templateType = template.type || "email";
  const currentLocale =
    shop.trackingPreferences?.notificationsDefaultLocale || "en";
  const defaultLocale = currentLocale;

  // Create a formatter with example values for preview
  const exampleValues: Record<string, string> = {};
  for (const variable of variables) {
    const value = variable.exampleValue;
    exampleValues[variable.id] =
      typeof value === "string" ? value : `{{${variable.id}}}`;
  }
  // Add shop name
  exampleValues["shop.name"] = shop.name;
  const formatter = createFormatter(exampleValues);

  const theme = shop.trackingPreferences?.emailsTheme || defaultEmailsTheme;
  const backgroundColor =
    theme?.backgroundColor || defaultEmailsTheme.backgroundColor;
  const cardColor = theme?.cardColor || defaultEmailsTheme.cardColor;
  const cardTextColor =
    theme?.cardTextColor || defaultEmailsTheme.cardTextColor;

  const tailwindConfig = {
    theme: {
      extend: {
        colors: {
          primary:
            shop.preferences?.customisation?.["primary-400"] ??
            shop.preferences?.customisation?.["primary_400"] ??
            "#000000",
          secondary: "#f1f5f9",
          border: "#e2e8f0",
          background: backgroundColor ?? "#ffffff",
          card: cardColor ?? "#ffffff",
        },
      },
    },
  };

  const mockParcel = createMockParcel(shop.id);

  const blockProps: BlockRenderProps = {
    defaultLocale,
    shop,
    theme,
    locale: currentLocale,
    status: "IN_TRANSIT",
    preview: "",
    carrier: { name: "Carrier" },
    order: { firstName: "Customer", lastName: "", email: "" },
    parcel: mockParcel,
    googleWalletURL: "#",
    appleWalletURL: "#",
    walletEnabled: false,
    links: { trackingPage: "#", giftConfig: "#" },
  };

  const beforeBlocks = (data?.beforeBlocks || []).map((block, index) =>
    renderBlock(
      "before",
      block,
      currentLocale,
      index,
      formatter,
      blockProps,
      templateType,
    ),
  );

  const insideBlocks = (data?.insideBlocks || []).map((block, index) =>
    renderBlock(
      "inside",
      block,
      currentLocale,
      index,
      formatter,
      blockProps,
      templateType,
    ),
  );

  const afterBlocks = (data?.afterBlocks || []).map((block, index) =>
    renderBlock(
      "after",
      block,
      currentLocale,
      index,
      formatter,
      blockProps,
      templateType,
    ),
  );

  // Variables with example values for the provider
  const variablesWithValues = variables.map((definition) => ({
    ...definition,
  }));

  return (
    <div
      className={cn(
        "h-full overflow-auto bg-editor-background bg-[radial-gradient(var(--border)_1px,transparent_1px)] [background-size:16px_16px] rounded-md",
        className,
      )}
    >
      <VariablesDictionaryProvider variablesDefinition={variablesWithValues}>
        <BlockThemeProvider theme={theme}>
          <div className="flex items-center justify-center p-4">
            <div
              style={{
                maxWidth: viewport === "mobile" ? 375 : 680,
                width: "100%",
              }}
              className="transition-all duration-300 ease-in-out"
            >
              <EmailMainContainer
                viewport={viewport}
                backgroundColor={backgroundColor ?? undefined}
                tailwindConfig={tailwindConfig}
                isEditor={true}
              >
                <BlockSection>{beforeBlocks}</BlockSection>
                <EmailCardContainer
                  theme={theme}
                  cardColor={cardColor ?? undefined}
                  cardTextColor={cardTextColor ?? undefined}
                  isEditor={true}
                >
                  <BlockSection>{insideBlocks}</BlockSection>
                </EmailCardContainer>
                <BlockSection>{afterBlocks}</BlockSection>
              </EmailMainContainer>
            </div>
          </div>
        </BlockThemeProvider>
      </VariablesDictionaryProvider>
    </div>
  );
}

function BlockSection({ children }: React.PropsWithChildren) {
  return <div className="flex flex-col gap-4">{children}</div>;
}

interface BlockRenderProps {
  defaultLocale: string;
  shop: unknown;
  theme: unknown;
  locale: string;
  status: string;
  preview: string;
  carrier: { name: string };
  order: { firstName: string; lastName: string; email: string };
  parcel: unknown;
  googleWalletURL: string;
  appleWalletURL: string;
  walletEnabled: boolean;
  links: { trackingPage: string; giftConfig: string };
}

function renderBlock(
  section: "before" | "inside" | "after",
  block: EmailTemplateBlockDto,
  currentLocale: string,
  index: number,
  formatter: Formatter,
  props: BlockRenderProps,
  templateType: string,
) {
  const blockType = block.type as BlockType;
  const blockConfig = getEditorBlockConfig(blockType);

  // Use web component for web templates, email component for email templates
  const Component =
    templateType === "web"
      ? blockConfig?.web?.component
      : blockConfig?.email?.component;

  if (Component) {
    // Cast props to expected types - these are render-only components
    // that accept various shop/parcel shapes
    return (
      <Component
        key={block.id}
        index={index}
        block={block as never}
        shop={props.shop as never}
        formatter={formatter}
        section={section}
        parcel={props.parcel as never}
        googleWalletURL={props.googleWalletURL}
        appleWalletURL={props.appleWalletURL}
        walletEnabled={props.walletEnabled}
        locale={currentLocale}
        defaultLocale={props?.defaultLocale}
        status={props.status}
        preview={props.preview}
        carrier={props.carrier}
        order={props.order}
        theme={props.theme as never}
        isPreview={true}
        formAPI={{
          blockPath: "",
          updateField: () => {},
          getFieldValue: () => undefined,
          registerImage: () => {},
        }}
        variables={[]}
      />
    );
  }

  return null;
}

// Minimal mock parcel for preview rendering
function createMockParcel(shopId: string) {
  return {
    id: "preview-parcel",
    trackingId: "PREVIEW123",
    orderId: "order-1",
    orderNumber: "1001",
    carrierCode: "COLISSIMO",
    customerEmail: "customer@example.com",
    customerLocale: "fr",
    shopId,
    createdAt: Date.now(),
    trackingNumbers: ["PREVIEW123"],
    fulfillmentId: "fulfillment-1",
    events: [
      {
        id: "event-1",
        parcelId: "preview-parcel",
        status: "IN_TRANSIT",
        createdAt: Date.now(),
        label: "",
        notifications: [],
      },
    ],
    lineItems: [
      {
        quantity: 1,
        name: "Example Product",
        price: 49.99,
        imageUrl: "https://placehold.co/100x100",
        sku: "SKU-001",
      },
    ],
    destination: {
      firstName: "John",
      lastName: "Doe",
      address1: "123 Main Street",
      address2: "",
      city: "Paris",
      zip: "75001",
      province: "",
      provinceCode: "",
      country: "France",
      countryCode: "FR",
      phone: "+33123456789",
      company: "",
      name: "John Doe",
    },
  };
}
